import React, { useEffect, useImperativeHandle, useState } from "react";
import { Text, View } from "react-native";
import { useForm } from "react-hook-form";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { FormButtons } from "app/features/residents/molecules/FormButtons";
import { Button, ButtonText } from "ui/gluestack/button";
import {
  useMedicationLogsFilterStorage,
  MedicationLogsFilterForm,
} from "../hooks/useMedicationLogsFilterStorage";

interface MedicationLogsFilterModalRef {
  show: (currentFilters?: MedicationLogsFilterForm) => void;
}

interface Props {
  onApplyFilters: (filters: MedicationLogsFilterForm) => void;
}

const MedicationLogsFilterModal = React.forwardRef<
  MedicationLogsFilterModalRef,
  Props
>(({ onApplyFilters }, ref) => {
  const [showing, setShowing] = useState(false);
  const { control, reset, handleSubmit, watch } = useForm<MedicationLogsFilterForm>({
    defaultValues: {
      date_from: undefined,
      date_to: undefined,
    },
  });

  const {
    activeQuickFilter,
    setActiveQuickFilter,
    calculateQuickFilterDates,
    saveQuickFilter,
    saveCustomFilters,
    clearFilters,
  } = useMedicationLogsFilterStorage((filters) => {
    reset(filters);
    onApplyFilters(filters);
  });
  
  // Watch for manual date changes to deactivate quick filter
  const watchedDates = watch();
  
  useEffect(() => {
    if (activeQuickFilter && watchedDates.date_from && watchedDates.date_to) {
      const expectedFilters = calculateQuickFilterDates(activeQuickFilter);
      // If dates don't match expected quick filter dates, user manually changed them
      if (
        watchedDates.date_from !== expectedFilters.date_from ||
        watchedDates.date_to !== expectedFilters.date_to
      ) {
        setActiveQuickFilter(undefined);
      }
    }
  }, [watchedDates.date_from, watchedDates.date_to, activeQuickFilter, calculateQuickFilterDates, setActiveQuickFilter]);

  useImperativeHandle(ref, () => ({
    show: (currentFilters?: MedicationLogsFilterForm) => {
      reset(currentFilters || { date_from: undefined, date_to: undefined });
      setShowing(true);
    },
  }));

  const handleClose = () => {
    setShowing(false);
  };

  const handleClearAll = async () => {
    const emptyFilters = { date_from: undefined, date_to: undefined };
    reset(emptyFilters);
    await clearFilters();
    onApplyFilters(emptyFilters);
    setShowing(false);
  };

  const onSubmit = async (data: MedicationLogsFilterForm) => {
    // If quick filter is active, save it; otherwise save custom filters
    if (activeQuickFilter) {
      await saveQuickFilter(activeQuickFilter);
    } else {
      await saveCustomFilters(data);
    }
    
    onApplyFilters(data);
    setShowing(false);
  };

  const handleQuickFilter = (days: number) => {
    const filters = calculateQuickFilterDates(days);
    setActiveQuickFilter(days);
    reset(filters);
  };

  return (
    <ReusableModal
      isVisible={showing}
      onClose={handleClose}
      title="Filter Medication Logs"
      className="w-full max-w-[600px] mx-auto"
    >
      <View className="mb-4">
        <Text className="text-sm font-medium mb-2 text-gray-600">
          Quick Filters
        </Text>
        <View className="flex flex-row gap-2">
          <Button
            variant={activeQuickFilter === 7 ? "solid" : "outline"}
            action={activeQuickFilter === 7 ? "primary" : "primary"}
            size="sm"
            onPress={() => handleQuickFilter(7)}
          >
            <ButtonText>Last 7 days</ButtonText>
          </Button>
          <Button
            variant={activeQuickFilter === 30 ? "solid" : "outline"}
            action={activeQuickFilter === 30 ? "primary" : "primary"}
            size="sm"
            onPress={() => handleQuickFilter(30)}
          >
            <ButtonText>Last 30 days</ButtonText>
          </Button>
        </View>
      </View>

      <View className="border-b border-gray-200 my-4" />

      <Text className="text-sm font-medium mb-2 text-gray-600">
        Custom Range
      </Text>
      <View className="grid md:grid-cols-2 gap-4">
        <FormControlledDatePicker<MedicationLogsFilterForm>
          id="date_from"
          name="date_from"
          control={control}
          label="From Date"
          placeholder="Select start date"
          rules={{
            validate: (value, formValues) => {
              if (value && formValues.date_to && value >= formValues.date_to) {
                return "From date must be before To date";
              }
              return true;
            },
          }}
        />
        <FormControlledDatePicker<MedicationLogsFilterForm>
          id="date_to"
          name="date_to"
          control={control}
          label="To Date"
          placeholder="Select end date"
          rules={{
            validate: (value, formValues) => {
              if (
                value &&
                formValues.date_from &&
                value <= formValues.date_from
              ) {
                return "To date must be after From date";
              }
              return true;
            },
          }}
        />
      </View>
      <FormButtons
        okTitle="Apply"
        cancelTitle="Clear All"
        onClose={handleClearAll}
        onSave={handleSubmit(onSubmit)}
      />
    </ReusableModal>
  );
});

MedicationLogsFilterModal.displayName = "MedicationLogsFilterModal";
export { MedicationLogsFilterModal };
export type { MedicationLogsFilterModalRef };
